"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, LockKeyhole, TrendingUp, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const commitments = [{
  icon: ShieldCheck,
  title: "Cumplimiento",
  subtitle: "Apego normativo y certeza jurídica",
  description: "Garantizamos estricta adhesión a los marcos legales, fiscales y de contabilidad gubernamental armonizada (CONAC), previniendo contingencias y asegurando solidez ante cualquier escrutinio institucional."
}, {
  icon: LockKeyhole,
  title: "Confianza",
  subtitle: "Discreción fiduciaria y lealtad profesional",
  description: "Protegemos el interés y la información sensible de nuestros clientes mediante reserva profesional absoluta, transparencia contractual y supervisión directa de socios senior en cada mandato."
}, {
  icon: TrendingUp,
  title: "Resultados",
  subtitle: "Orden comprobable y cuentas solventadas",
  description: "Transformamos desafíos regulatorios en dictámenes favorables, cuentas públicas solventadas, ahorros operativos sostenibles y estabilidad patrimonial medible para el sector público y empresarial."
}];
export default function Compromisos() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="compromisos" data-nav="light" className="relative bg-background py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block text-xs font-bold tracking-widest text-muted-foreground uppercase">
            Estrategia · Cumplimiento · Resultados
          </span>
          <h2 className="mt-3 font-serif text-3xl font-semibold tracking-wider text-foreground uppercase sm:text-4xl">
            Nuestros compromisos institucionales
          </h2>
          <div className="mx-auto mt-4 h-[1.5px] w-12 bg-chart-2" />
          <p className="mt-4 font-sans text-base text-muted-foreground sm:text-lg">
            Principios rectores que fundamentan cada dictamen, auditoría y resolución estratégica encomendada a nuestra firma.
          </p>
        </div>

        {/* Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-10">
          {commitments.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} data-index={i} variants={itemVariants} className="group relative flex flex-col justify-between border border-border bg-card p-8 transition-[border-color,box-shadow] duration-300 hover:border-chart-2/60 hover:shadow-md">
                {/* Top Hairline Indicator */}
                <div className="absolute inset-x-0 top-0 h-[2px] bg-border transition-colors duration-300 group-hover:bg-chart-2" />

                <div>
                  {/* Institutional Icon Badge */}
                  <div className="flex size-12 items-center justify-center border border-border bg-background transition-colors duration-300 group-hover:border-chart-2">
                    <IconComponent className="size-6 text-foreground transition-colors duration-300 group-hover:text-chart-2" strokeWidth={1.75} />
                  </div>

                  {/* Title & 48px Satin Gold Hairline */}
                  <div className="mt-8">
                    <h3 className="font-serif text-xl font-semibold tracking-wide text-foreground uppercase">
                      {pillar.title}
                    </h3>
                    <div className="mt-2.5 h-[1.5px] w-12 bg-chart-2" />
                    <p className="mt-3 font-sans text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                      {pillar.subtitle}
                    </p>
                  </div>

                  {/* Commitment Statement */}
                  <p className="mt-4 font-sans text-sm leading-relaxed text-muted-foreground">
                    {pillar.description}
                  </p>
                </div>

                {/* Footer Metadata Indicator */}
                <div className="mt-8 border-t border-border pt-4">
                  <span className="font-mono text-xs text-muted-foreground">
                    ESTATUTO 0{i + 1}
                  </span>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Directive & Call to Action */}
        <div className="mt-16 border-t border-border pt-10 text-center">
          <p className="font-sans text-sm text-muted-foreground">
            Asesoría multidisciplinaria respaldada por especialistas senior y dirección institucional.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Button asChild className="rounded-xs border border-chart-2 bg-primary px-8 py-3 text-xs font-semibold tracking-widest text-primary-foreground uppercase shadow-xs transition-colors duration-200 hover:bg-chart-2 hover:text-primary">
              <Link to="/ContactPage">
                <span>Solicitar Asesoría</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-xs border-primary px-7 py-3 text-xs font-semibold tracking-widest text-primary uppercase shadow-none transition-colors duration-200 hover:bg-primary/5 hover:text-primary">
              <Link to="/OnlineConsultingPage">
                Hablar con un especialista
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}